import {Component, HostListener, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslocoService} from '@ngneat/transloco';
import {forkJoin} from 'rxjs';
import {AuthGuard} from 'src/app/common/auth-guard.service';
import {ComponentCanDeactivate} from 'src/app/common/can-deactivate/component-can-deactivate';
import {AlarmDefinitionSummary} from 'src/app/model/alarm-definition-summary';
import {AlarmNotificationType} from 'src/app/model/alarm-notification-type';
import {AlarmSubscription} from 'src/app/model/alarm-subscription';
import {Organization} from 'src/app/model/organization';
import {AlarmDefinitionSummaryResponse} from 'src/app/model/responses/alarm-definition-summary-response';
import {AlarmsService} from 'src/app/services/alarms.service';
import {CommonMessagesService} from 'src/app/services/common-messages.service';
import {OrganizationsService} from 'src/app/services/organizations.service';
import {SubscriptionData} from "../../../model/subscription-data";
import {AlarmSubscriptionPersistRequest} from "../../../model/requests/alarm-subscription-persist-request";
import * as moment from "moment-timezone";
import {CommonTranslationsService} from "../../../services/common-translations.service";

@Component({
  selector: 'app-organization-subscriptions',
  templateUrl: './organization-subscriptions.component.html',
  styleUrls: ['./organization-subscriptions.component.scss']
})
export class OrganizationSubscriptionsComponent implements OnInit, ComponentCanDeactivate {

  public languageOptions: any[];
  public timeZoneOptions: any[];
  public selectedLanguage: any;
  public additionalMails: string[];

  public organizationSubscriptions: SubscriptionData[] = new Array(10);
  public totalOrganizationSubscriptions: number = 0;
  public organizationDataLoaded: boolean = false;
  public operationsTranslation: { [key: string]: string } = {};
  public notificationTypeTranslation: { [key: string]: string } = {};

  public organizationForm: FormGroup;
  public persistingChanges: boolean = false;

  public rows: number = 10;
  public first: number = 0;

  private static readonly DEFAULT_LANGUAGE: string = 'es';

  private emailPattern: RegExp;

  private organizationUuid: string;
  private organization: Organization;

  private formSuccess: boolean = false;
  private hasChanged: boolean = false;


  constructor(
    private readonly commonTranslationsService: CommonTranslationsService,
    private readonly organizationsService: OrganizationsService,
    private readonly alarmsService: AlarmsService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly commonMessagesService: CommonMessagesService,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly i18n: TranslocoService,
    public authGuard: AuthGuard,
  ) {
    this.languageOptions = this.commonTranslationsService.getLanguageOptions();
    this.timeZoneOptions = moment.tz.names().map(zoneName => ({name: zoneName}));
    this.emailPattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    this.additionalMails = [];
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.loadOrganizationData(params.organizationUuid);
    });

    this.i18n.selectTranslation().subscribe(() => {
      this.buildNotificationTypeTranslation();
    });
    this.buildOperationsTranslation();
  }

  private createForm(organization: Organization): void {
    this.organizationForm = this.fb.group({
      name: [{value: organization.name, disabled: true}, [Validators.required]],
      email: [{value: organization.email, disabled: true}, [Validators.required]],
      mailTimezone: [organization.mailTimezone, [Validators.required]],
    });
  }

  private loadOrganizationData(organizationUuid: string): void {
    this.organizationDataLoaded = false;

    this.organizationUuid = organizationUuid;
    const queries = {
      organizationQuery: this.organizationsService.getOrganization(organizationUuid),
      alarmsDefinition: this.alarmsService.getAlarmsDefinitionSummary(organizationUuid),
      organizationSubscriptions: this.alarmsService.getAlarmSubscriptionsByOrganizationUuid(organizationUuid)
    }

    forkJoin(queries).subscribe({
      next: (response: {
        organizationQuery: { organization: Organization },
        alarmsDefinition: AlarmDefinitionSummaryResponse,
        organizationSubscriptions: { alarmSubscriptions: AlarmSubscription[] }
      }) => {
        this.organization = response.organizationQuery.organization;
        if (this.organization.mailLanguage == null) {
          this.organization.mailLanguage = OrganizationSubscriptionsComponent.DEFAULT_LANGUAGE;
        }
        this.selectedLanguage = this.languageOptions.find(lang => lang.langCode === this.organization.mailLanguage);
        if (this.organization.additionalMails != null) {
          this.additionalMails = this.organization.additionalMails;
        }

        this.createForm(this.organization);
        const alarmsDefinition: AlarmDefinitionSummary[] = response.alarmsDefinition.alarmsDefinition;
        const organizationSubscriptions: {
          [key: string]: AlarmSubscription
        } = response.organizationSubscriptions.alarmSubscriptions.reduce((carry, s) => {
          carry[s.alarmDefinitionUuid] = s;
          return carry;
        }, {});
        this.organizationSubscriptions = alarmsDefinition.map(alarm => {
          const organizationSubscription: SubscriptionData = {
            alarmNotificationType: null,
            ...organizationSubscriptions[alarm.alarmDefinitionUuid],
            alarmDefinitionUuid: alarm.alarmDefinitionUuid,
            alarmDefinitionName: alarm.name,
            deviceMeasurement: alarm.deviceMeasurement,
            operation: alarm.operation,
            value: alarm.value,
            timeWindow: alarm.timeWindow,
            organizationOwner: alarm.organizationOwner,
          }
          return organizationSubscription;
        });
        this.totalOrganizationSubscriptions = this.organizationSubscriptions.length;
        this.organizationDataLoaded = true;
      },
      error: error => {
        if (error.status == 404) {
          this.router.navigate(['/not-found']);
        }
      }
    })
  }

  private buildNotificationTypeTranslation(): void {
    this.notificationTypeTranslation[AlarmNotificationType.INSTANTANEOUS] = this.i18n.translate('common.instantaneous');
    this.notificationTypeTranslation[AlarmNotificationType.AGGREGATED] = this.i18n.translate('common.aggregated');
    this.notificationTypeTranslation['NONE'] = this.i18n.translate('common.doNotNotify');
  }

  private buildOperationsTranslation(): void {
    this.commonTranslationsService.getOperationsTranslation().subscribe(operationsTranslation => {
      this.operationsTranslation = operationsTranslation;
    });
  }

  public checkEmailAndPersistChanges(): void {
    if (this.organization.email == null) this.commonMessagesService.showRequestConfirmation('info.subscriptionNoEmail', () => {
      this.persistSubscriptionsData()
    });
    else this.persistSubscriptionsData();
  }

  public persistSubscriptionsData(): void {
    this.persistingChanges = true;
    const subscriptions = this.buildSubscriptionsData();
    const formValue = this.organizationForm.getRawValue();
    let alarmSubscriptionPersistRequest: AlarmSubscriptionPersistRequest = {
      subscriptions: subscriptions,
      mailLanguage: this.selectedLanguage.langCode,
      mailTimezone: formValue.mailTimezone,
      additionalMails: this.additionalMails
    }
    this.alarmsService.subscribeOrganizationToAlarms(this.organizationUuid, alarmSubscriptionPersistRequest).subscribe({
      next: () => {
        this.commonMessagesService.showSuccessfullyUpdatedMessage();
        this.formSuccess = true;
        this.router.navigate(['/organizations']);
      },
      error: () => {
        this.commonMessagesService.showUnexpectedPersistingError('edit');
        this.persistingChanges = false;
      }
    })
  }

  private buildSubscriptionsData(): { [key: string]: AlarmNotificationType } {
    return this.organizationSubscriptions
      .filter(o => o.alarmNotificationType != null)
      .reduce((carry, s) => {
        carry[s.alarmDefinitionUuid] = s.alarmNotificationType;
        return carry;
      }, {});
  }

  public changeNotificationType(organizationSubscription: SubscriptionData): void {
    if (organizationSubscription.alarmNotificationType === AlarmNotificationType.INSTANTANEOUS) {
      organizationSubscription.alarmNotificationType = AlarmNotificationType.AGGREGATED;
    } else if (organizationSubscription.alarmNotificationType === AlarmNotificationType.AGGREGATED) {
      organizationSubscription.alarmNotificationType = null;
    } else if (organizationSubscription.alarmNotificationType === null) {
      organizationSubscription.alarmNotificationType = AlarmNotificationType.INSTANTANEOUS;
    }
    this.hasChanged = true;
  }

  public validateEmail(event): void {
    if (!this.emailPattern.test(event.value)) {
      this.additionalMails.pop();
    }
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || (!this.organizationForm.dirty && !this.hasChanged &&
      this.organization.mailLanguage == this.selectedLanguage.langCode);
  }

  protected readonly AlarmNotificationType = AlarmNotificationType;

}
